/**
 * <gh-sticky-atc> — sticky "Add to cart" bar of the product page (snippets/gh-sticky-atc.liquid).
 *
 * - Shows when the buy box "Add to cart" button leaves the screen upwards (IntersectionObserver)
 *   and hides when it is visible again. Also hidden while the footer is on screen and while any
 *   modal dialog is open (cart drawer, size guide, search...), so it never covers them.
 * - The bar shows the selected variant and price: Horizon re-renders the section when the size
 *   changes, and the new bar is morphed in (same approach as Horizon's own sticky bar).
 * - The button adds the selected variant by clicking the buy box button. With no size selected it
 *   scrolls to the size chips instead of adding.
 */
import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

const RESIZE_DEBOUNCE_MS = 150;

class GhStickyAtc extends Component {
  requiredRefs = ['bar', 'button'];

  /** @type {IntersectionObserver | null} */
  #buyObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  /** @type {Element | null} */
  #observedTarget = null;

  #buyButtonAbove = false;
  #footerVisible = false;
  #resizeTimer = 0;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartDone, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartDone, { signal });
    window.addEventListener('resize', this.#handleResize, { signal });

    this.#observeBuyButton();
    this.#observeFooter();

    this.#dialogObserver = new MutationObserver(() => this.#render());
    this.#dialogObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#buyObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#dialogObserver?.disconnect();
    clearTimeout(this.#resizeTimer);
  }

  /** The buy box "Add to cart" button of this product */
  #getTarget() {
    const section = this.closest('.shopify-section');
    const form = section?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    return /** @type {HTMLButtonElement | null} */ (form?.querySelector('[ref="addToCartButton"]') ?? null);
  }

  /** Height of the sticky header: the button is "off screen" once it slides under it */
  #getHeaderOffset() {
    const header = document.querySelector('#header-component');
    if (!header || header.getAttribute('sticky') !== 'always') return 0;
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  }

  #observeBuyButton() {
    const target = this.#getTarget();
    this.#buyObserver?.disconnect();
    this.#observedTarget = target;
    if (!target) return;

    this.#buyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        // Above the viewport (scrolled past), not below it (not reached yet)
        this.#buyButtonAbove = !entry.isIntersecting && entry.boundingClientRect.top < entry.rootBounds?.top;
        this.#render();
      },
      { rootMargin: `-${this.#getHeaderOffset()}px 0px 0px 0px` }
    );
    this.#buyObserver.observe(target);
  }

  #observeFooter() {
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(([entry]) => {
      this.#footerVisible = Boolean(entry?.isIntersecting);
      this.#render();
    });
    this.#footerObserver.observe(footer);
  }

  #handleResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = window.setTimeout(() => this.#observeBuyButton(), RESIZE_DEBOUNCE_MS);
  };

  #hasOpenDialog() {
    return Boolean(document.querySelector('dialog[open]'));
  }

  #render() {
    const { bar } = this.refs;
    if (!bar) return;
    const show = this.#buyButtonAbove && !this.#footerVisible && !this.#hasOpenDialog();
    const was = bar.dataset.stuck === 'true';
    if (show === was) return;
    bar.dataset.stuck = String(show);
    bar.toggleAttribute('inert', !show);
  }

  /** Is there an option without a selected value in the buy box picker? */
  #needsSelection() {
    if (this.dataset.hasOptions !== 'true') return false;
    const picker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (!picker) return false;
    const fieldsets = picker.querySelectorAll('fieldset');
    for (const fieldset of fieldsets) {
      if (!fieldset.querySelector('input:checked')) return true;
    }
    for (const select of picker.querySelectorAll('select')) {
      if (!select.value) return true;
    }
    return false;
  }

  #scrollToChips() {
    const picker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (!picker) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    picker.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    const firstChip = /** @type {HTMLElement | null} */ (picker.querySelector('input[type="radio"]:not(:disabled)'));
    firstChip?.focus({ preventScroll: true });
  }

  handleClick = () => {
    const { button } = this.refs;
    if (!button || button.disabled) return;

    if (this.#needsSelection()) {
      this.#scrollToChips();
      return;
    }

    const target = this.#getTarget();
    if (!target) return;
    target.dataset.puppet = 'true';
    target.click();
  };

  #handleCartDone = () => {
    const target = this.#getTarget();
    if (target) target.dataset.puppet = 'false';
  };

  /**
   * Horizon re-renders the section when a size is picked: take the new bar from that HTML.
   * @param {Event & { promise?: Promise<any> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && String(detail.productId) !== this.dataset.productId) return;

        const next = /** @type {HTMLElement | null} */ (detail.html.querySelector('gh-sticky-atc'));
        const nextBar = next?.querySelector('[ref="bar"]');
        if (!next || !nextBar) return;

        const { bar } = this.refs;
        const stuck = bar.dataset.stuck;
        const inert = bar.hasAttribute('inert');
        morph(bar, nextBar, { childrenOnly: true });
        bar.dataset.stuck = stuck ?? 'false';
        bar.toggleAttribute('inert', inert);
        this.dataset.variantAvailable = next.dataset.variantAvailable ?? 'false';

        // No variant for the chosen options (or no size yet): say so instead of the first variant's size
        if (detail.resource == null && this.dataset.chooseLabel) {
          const variantLabel = this.querySelector('.gh-sticky-atc__variant');
          if (variantLabel) variantLabel.textContent = this.dataset.chooseLabel;
        }

        // The buy box may have been re-rendered too
        if (this.#getTarget() !== this.#observedTarget) this.#observeBuyButton();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[gh-sticky-atc] variant update failed:', error);
      });
  };
}

if (!customElements.get('gh-sticky-atc')) {
  customElements.define('gh-sticky-atc', GhStickyAtc);
}
